import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  unseenSupportReplyIds,
  type NewSupportTicketInput,
  type SupportTicketDetail,
  type SupportTicketSummary,
} from '@mustauto/ui/web';
import { api } from '@/lib/api';

const TICKETS_KEY = ['support-tickets'] as const;

/**
 * Chamados de suporte do gestor no browser. Até então a resposta da equipe só
 * chegava por e-mail (o sino in-app é do mobile) — aqui ela aparece no painel.
 */
export function useSupportTickets() {
  const queryClient = useQueryClient();

  const ticketsQuery = useQuery<{ tickets: SupportTicketSummary[] }>({
    queryKey: TICKETS_KEY,
    queryFn: async () => (await api.get('/support/tickets')).data,
    // resposta do suporte não tem push no web; um poll leve mantém o aviso vivo
    refetchInterval: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: async (input: NewSupportTicketInput) => {
      const res = await api.post('/support/tickets', input);
      return res.data as { ticketId: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TICKETS_KEY });
    },
  });

  return {
    tickets: ticketsQuery.data?.tickets ?? [],
    isLoading: ticketsQuery.isLoading,
    isError: ticketsQuery.isError,
    refetch: ticketsQuery.refetch,
    createTicket: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
}

export function useSupportTicket(ticketId: string) {
  const queryClient = useQueryClient();

  const ticketQuery = useQuery<SupportTicketDetail>({
    queryKey: ['support-ticket', ticketId],
    queryFn: async () => (await api.get(`/support/tickets/${ticketId}`)).data,
    enabled: !!ticketId,
  });

  const replyMutation = useMutation({
    mutationFn: async (content: string) => {
      const res = await api.post(`/support/tickets/${ticketId}/messages`, { content });
      return res.data as { messageId: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: TICKETS_KEY });
    },
  });

  return {
    ticket: ticketQuery.data,
    isLoading: ticketQuery.isLoading,
    isError: ticketQuery.isError,
    refetch: ticketQuery.refetch,
    reply: replyMutation.mutateAsync,
    isReplying: replyMutation.isPending,
  };
}

/**
 * Contador do badge na sidebar — compartilha o cache da lista de chamados.
 * `enabled: false` na sessão demo, que é read-only e não fala com o suporte.
 */
export function useUnseenSupportReplies(enabled = true): number {
  const { data } = useQuery<{ tickets: SupportTicketSummary[] }>({
    queryKey: TICKETS_KEY,
    queryFn: async () => (await api.get('/support/tickets')).data,
    refetchInterval: 60_000,
    enabled,
  });
  return unseenSupportReplyIds(data?.tickets ?? []).length;
}

/** Invalida a lista para o badge recalcular depois de marcar um ticket visto. */
export function useRefreshSupportBadge() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: TICKETS_KEY });
}
